import _ from 'lodash';
import { defineStore } from 'pinia';
import { set, del, computed } from 'vue-demi';

import { useSessionViewsRouter } from './use-session-views-router';
import { SessionView } from './types';
import { DEFAULT_VIEW_SETTINGS } from './constants';

export const useSessionViewsStore = defineStore('HITL-Session-Views', {
  state: () => {
    return {
      userId: '',
      viewsById: {} as Record<string, SessionView>,
      isDataReady: false,
      /* When action potentially affects more than one item */
      isUpdating: false,
      /* Contains view ID that is being updated */
      isUpdatingId: '',
      isDeletingId: '',
    };
  },
  getters: {
    isLoadingInitialData: state => !state.isDataReady,
    views: state => {
      const views = Object.values(state.viewsById);
      return _.orderBy(views, ['userId', 'order'], ['asc', 'asc']);
    },
    getViewById: state => (id: string) => state.viewsById[id],
    isAnyUpdating: state => state.isUpdating || !!state.isUpdatingId,
    isDeleting: state => !!state.isDeletingId,
    isSessionViewListEmpty(): boolean {
      return !this.views.length;
    },
    maxCommonOrder(): number {
      const item = _.maxBy(this.commonViews, item => item.order);
      return item ? item.order : 0;
    },
    maxAgentOrder(): number {
      const item = _.maxBy(this.agentViews, item => item.order);
      return item ? item.order : 0;
    },
    defaultViewId(): string {
      const result = this.views.find(item => item.isDefault);
      return result ? result.id : this.views[0]?.id || '';
    },
    commonViews(): SessionView[] {
      return this.views.filter(item => !item.userId);
    },
    agentViews(): SessionView[] {
      return this.views.filter(item => item.userId);
    },
  },
});

/** TODO: Can be merged with store after "getCurrentInstance" is removed from "useRouter" */
export const useSessionViewsGetters = () => {
  const store = useSessionViewsStore();
  const { activeSessionViewId } = useSessionViewsRouter();

  const activeSessionView = computed(() => {
    return store.viewsById[activeSessionViewId.value];
  });

  const activeFilterLabel = computed(() => {
    return store.views.find(view => view.id === activeSessionViewId.value)?.name;
  });

  /** Replace agent ID placeholder with active agent ID */
  const activeConversationFilter = computed(() => {
    if (!activeSessionView.value) {
      return DEFAULT_VIEW_SETTINGS.conversations;
    }

    const conversations = activeSessionView.value.settings.conversations || {};
    const settings = {
      ...conversations,
      claimedBy: conversations.claimedBy ? store.userId : '',
      claimedByHistory: conversations.claimedByHistory ? store.userId : '',
    };

    return settings || DEFAULT_VIEW_SETTINGS.conversations;
  });

  return { activeSessionView, activeConversationFilter, activeFilterLabel };
};

export const useSessionViewsSetters = () => {
  const store = useSessionViewsStore();

  const setViews = (views: SessionView[]) => {
    store.viewsById = _.keyBy(views, 'id');
  };

  const addOrUpdateView = (view: SessionView) => {
    const current = store.viewsById[view.id];
    const result = { ...current, ...view };
    set(store.viewsById, view.id, result);
  };

  const deleteView = (id: string) => del(store.viewsById, id);
  return { setViews, addOrUpdateView, deleteView };
};
